import { useEffect, useState } from "react";
import { spanToLabel } from "../constants";

/**
 * What the chart engine shows, from engine.events.onVisibleRangeChange: the
 * label of the visible span for the zoom button (updates on any zoom, resize
 * or timeframe) and whether the latest bar is in view. Re-renders only when
 * one of the returned values changes, not on every frame of a pan.
 *
 * @param {object|null} engine - ChartEngine from useChartEngine
 * @returns {{ span: { label: string, seconds: number }|null, isLastBarVisible: boolean, priceAxisWidth: number }}
 *   span: label "M21", "H8"… and seconds when the label was set, null until the engine
 *   knows the span; priceAxisWidth: CSS px
 */
const useVisibleSpan = (engine) => {
  const [span, setSpan] = useState(null);
  const [isLastBarVisible, setIsLastBarVisible] = useState(true);
  const [priceAxisWidth, setPriceAxisWidth] = useState(0);

  useEffect(() => {
    if (!engine) return undefined;

    const unsubscribe = engine.events.onVisibleRangeChange((range) => {
      const label = spanToLabel(range.spanSeconds);

      // A new object only for a new label: the seconds change on every frame of a zoom
      setSpan((current) =>
        current?.label === label || !label ? current : { label, seconds: range.spanSeconds },
      );
      setIsLastBarVisible(range.isLastBarVisible);
      setPriceAxisWidth(range.priceAxisWidth);
    });

    return () => {
      unsubscribe();
      setSpan(null);
      setIsLastBarVisible(true);
    };
  }, [engine]);

  return { span, isLastBarVisible, priceAxisWidth };
};

export default useVisibleSpan;
